import {
  Button,
  ButtonsGroup,
  Modal,
  ModalProps,
  Text,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { BlockCreateInvoiceActionReason } from '../../enums/blockCreateInvoiceActionReason'
import { InvoiceDocumentType } from '../../enums/invoiceDocumentType'
import { useDeleteSalesDocumentDraft } from '../../hooks/useDeleteSalesDocumentDraft'
import type { RelatedInvoice } from '../../types/relatedInvoice'
import { getRelatedInvoiceRoute } from '../../utils/depositInvoices/getRelatedInvoiceRoute'
import { reasonToTranslationKey } from './constants/reasonToTranslationKey'

interface BlockCreateInvoiceActionModalProps extends ModalProps {
  blockingReason: BlockCreateInvoiceActionReason
  createInvoiceUrl?: string
  depositInvoiceDraftId?: number
  depositInvoiceId?: number
  existingFinalInvoiceDraft?: RelatedInvoice
  onCreateDepositInvoiceClick?: () => void
  quoteId?: number
  regularInvoiceDraftId?: number
}

const BlockCreateInvoiceActionModalComponent = ({
  blockingReason,
  createInvoiceUrl,
  depositInvoiceDraftId,
  depositInvoiceId,
  existingFinalInvoiceDraft,
  id,
  onCreateDepositInvoiceClick,
  quoteId,
  regularInvoiceDraftId,
}: BlockCreateInvoiceActionModalProps): ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { organization } = useUserOrganization()
  const { close } = useModal(id)
  const { closeButton, confirmButton, description, title } = reasonToTranslationKey[blockingReason]

  // DepositInvoiceDraftExists deletes the deposit draft then navigates to the new regular invoice;
  // RegularInvoiceDraftExists deletes the regular draft then proceeds with deposit creation.
  const draftIdToDelete =
    blockingReason === BlockCreateInvoiceActionReason.RegularInvoiceDraftExists
      ? regularInvoiceDraftId
      : depositInvoiceDraftId

  const { deleteSalesDocumentDraft, isProcessing } = useDeleteSalesDocumentDraft({
    onSuccess: () => {
      close()
      if (blockingReason === BlockCreateInvoiceActionReason.RegularInvoiceDraftExists) {
        onCreateDepositInvoiceClick?.()
      } else if (createInvoiceUrl) {
        navigate(createInvoiceUrl)
      }
    },
  })

  const goToDepositInvoice = useCallback(() => {
    if (!depositInvoiceId) {
      return
    }

    const depositInvoiceRoute = reactRoute.organization.invoices.getViewInvoiceRoute(
      depositInvoiceId.toString(),
      InvoiceDocumentType.Invoice,
    )

    close()
    navigate(getOrganizationViewUrl(organization?.url, depositInvoiceRoute))
  }, [close, depositInvoiceId, navigate, organization?.url])

  const goToFinalInvoice = useCallback(() => {
    if (!existingFinalInvoiceDraft) {
      return
    }

    const finalInvoiceRoute = getRelatedInvoiceRoute(existingFinalInvoiceDraft)

    close()
    navigate(getOrganizationViewUrl(organization?.url, finalInvoiceRoute))
  }, [close, existingFinalInvoiceDraft, navigate, organization?.url])

  const deleteDraftAndProceed = useCallback(() => {
    if (!draftIdToDelete) {
      return
    }

    deleteSalesDocumentDraft({
      approvedEstimateId: quoteId?.toString(),
      documentId: draftIdToDelete,
      documentType: InvoiceDocumentType.InvoiceDraft,
    })
  }, [deleteSalesDocumentDraft, draftIdToDelete, quoteId])

  const handleCloseButtonClick = useCallback(() => close(), [close])

  const handleConfirmButtonClick = useCallback(() => {
    switch (blockingReason) {
      case BlockCreateInvoiceActionReason.FinalInvoiceDraftAlreadyExists:
        goToFinalInvoice()
        break
      case BlockCreateInvoiceActionReason.DepositInvoiceUnpaid:
        goToDepositInvoice()
        break
      case BlockCreateInvoiceActionReason.DepositInvoiceDraftExists:
      case BlockCreateInvoiceActionReason.RegularInvoiceDraftExists:
        deleteDraftAndProceed()
        break
    }
  }, [blockingReason, deleteDraftAndProceed, goToDepositInvoice, goToFinalInvoice])

  return (
    <Modal id={id} size="s">
      <Modal.Header title={t(title)} />
      <Modal.Body>
        <Text>{t(description)}</Text>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button onClick={handleCloseButtonClick} variant="text">
            {t(closeButton)}
          </Button>
          <Button loading={isProcessing} onClick={handleConfirmButtonClick}>
            {t(confirmButton)}
          </Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}

export const BlockCreateInvoiceActionModal = withModalConditionalRender(BlockCreateInvoiceActionModalComponent)
